import { useEffect, useState, useRef } from 'react';
import cn from 'classnames';

import { useAppSelector, useAppDispatch } from 'store';
import { addMessage, updateLastBotMessageOrAdd, setThreadId } from 'store/messages';
import { ChatText, ChatWithMessages, Header } from 'features/technographics';
import { Text } from 'shared/components/ui';
import { environment } from 'environments/environment';

import styles from './index.module.scss';

interface Message {
	id: string;
	text: string;
	sender: 'user' | 'bot';
	timestamp: Date;
}

export const Chat = () => {
	const [value, setValue] = useState<string>('');
	const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const [tags, setTags] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const dispatch = useAppDispatch();
	const { threadId, messages } = useAppSelector(state => state.messages);

	const handleChange = (value: string) => {
		setValue(value);
	};

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const sendMessage = async (userMessage: string) => {
		try {
			setIsLoading(true);
			const response = await fetch(`${environment.apiUrl}/chat/`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'text/event-stream'
				},
				body: JSON.stringify({
					message: userMessage,
					...(threadId && { thread_id: threadId })
				})
			});

			const reader = response.body?.getReader();
			const decoder = new TextDecoder();

			if (!reader) return;

			let currentResponse = '';

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				const chunk = decoder.decode(value);
				const lines = chunk.split('\n\n');

				for (const line of lines) {
					if (line.startsWith('data: ')) {
						const data = JSON.parse(line.replace('data: ', ''));
						if (data.thread_id) {
							dispatch(setThreadId(data.thread_id));
						} else if (data.text) {
							currentResponse = currentResponse + data.text;
							setIsLoading(false);
							dispatch(updateLastBotMessageOrAdd(currentResponse));
						}
					}
				}
			}
			const isFinal = !currentResponse.includes(':');
			setButtonsDisabled(false);
			if (isFinal) setTags(currentResponse.split('\n').map(tag => tag.trim()));
		} catch (error) {
			setIsLoading(false);
			console.error('Fetch error:', error);
		}
	};

	useEffect(() => {
		if (messages.length > 0) {
			const lastMessage = messages[messages.length - 1];
			if (lastMessage.sender === 'bot') {
				if (!lastMessage.text.includes(':')) {
					setTags(lastMessage.text.split('\n').map(tag => tag.trim()));
				}
			}
		}
	}, []);

	const handleSubmit = () => {
		if (buttonsDisabled) return;

		setButtonsDisabled(true);

		const userMessage: Message = {
			id: Date.now().toString(),
			text: value,
			sender: 'user',
			timestamp: new Date()
		};

		dispatch(addMessage(userMessage));
		setValue('');
		sendMessage(userMessage.text);
	};

	const handleSubmitPopular = (text: string) => () => {
		setButtonsDisabled(true);

		const userMessage: Message = {
			id: Date.now().toString(),
			text: text,
			sender: 'user',
			timestamp: new Date()
		};

		dispatch(addMessage(userMessage));
		setValue('');
		sendMessage(userMessage.text);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	};

	return (
		<>
			{messages.length === 0 ? (
				<>
					<Header progress={'chat'} />
					<div className={styles.chat}>
						<div className={styles.chatBody}>
							<Text variant="inter/22/medium">Start by building a list of skills</Text>
							<ChatText
								className={styles.chatInput}
								value={value}
								onChange={handleChange}
								variant="main"
								onKeyDown={handleKeyDown}
								suffix={
									<div
										onClick={handleSubmit}
										className={cn(styles.button, { [styles.buttonDisabled]: buttonsDisabled })}>
										<Text variant="inter/15/semi" color="white">
											Submit
										</Text>
									</div>
								}
							/>
							<div className={styles.popular}>
								<div
									className={styles.popularWrapper}
									onClick={handleSubmitPopular('Data pipeline developer tools')}>
									<Text variant="inter/14/regular">Data pipeline developer tools</Text>
								</div>
								<div className={styles.popularWrapper} onClick={handleSubmitPopular('Graph databases')}>
									<Text variant="inter/14/regular">Graph databases</Text>
								</div>
								<div
									className={styles.popularWrapper}
									onClick={handleSubmitPopular('Machine learning and AI frameworks')}>
									<Text variant="inter/14/regular">Machine learning and AI frameworks</Text>
								</div>
								<div
									className={styles.popularWrapper}
									onClick={handleSubmitPopular('All kinds of Google Cloud services and systems')}>
									<Text variant="inter/14/regular">
										All kinds of Google Cloud services and systems
									</Text>
								</div>
							</div>
						</div>
					</div>
				</>
			) : (
				<ChatWithMessages
					sendMessage={sendMessage}
					isLoading={isLoading}
					tags={tags}
					setTags={setTags}
					buttonsDisabled={buttonsDisabled}
					setButtonsDisabled={setButtonsDisabled}
				/>
			)}
		</>
	);
};
